import {useCallback, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {IconLoadError} from '../iconGenerator/loadIconFile'
import {loadCreativeImageFile} from './loadCreativeImageFile'
import {addBreadcrumb} from '../../../common/errors/breadcrumb'
import {reportError} from '../../../common/errors/reportError'
import {trackEvent} from '../../../common/analytics/trackEvent'

/** Validates + decodes a picked or dropped file; `slot` is only used for analytics/error context. */
export const useCreativeImageLoader = (slot: string, onImageLoaded: (image: HTMLImageElement) => void) => {
  const {t} = useTranslation()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadFile = useCallback(
    async (file: File, source: 'pick' | 'drop') => {
      setError(null)
      setBusy(true)
      addBreadcrumb('creative-assets', 'image-picked', 'info', {slot, source, size: file.size, type: file.type})
      trackEvent('creative_assets_image_uploaded', {slot, source, format: file.type, size_bytes: file.size})
      try {
        onImageLoaded(await loadCreativeImageFile(file))
      } catch (err) {
        if (err instanceof IconLoadError) {
          setError(t(`creativeAssets.upload.${err.key}`))
        } else {
          reportError(err, 'creative-assets-load', {slot, size: file.size, type: file.type})
          setError(t('creativeAssets.upload.failed'))
        }
      } finally {
        setBusy(false)
      }
    },
    [onImageLoaded, slot, t],
  )

  return {busy, error, loadFile}
}
